import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { Plus, Loader2, Pencil } from 'lucide-react';
import {
  UniversalPage,
  UniversalTable,
  Card,
  Button,
  Input,
  StatusBadge,
  Modal,
  ModalBody,
  ModalFooter,
  type UniversalTableColumn,
  SearchField,
} from '@afrilink/ui';
import { useTableFilters } from '@/shared/hooks/useTableFilters';
import { salesService, type SalesCompany } from '@/domains/sales/services/salesService';
import { useStaffRole } from '@/shared/hooks/useStaffRole';

/**
 * SalesCompaniesPage — admin manages `sales_company` rows.
 * Admin-only Create-Company form (RLS enforces). Inline edit still v1.1.
 */

export default function SalesCompaniesPage() {
  const { isAdmin } = useStaffRole();
  const [rows, setRows] = useState<SalesCompany[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<SalesCompany | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const data = await salesService.listCompanies();
      setRows(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  /**
   * SEARCH ONLY — a partner roster, and §4.2 classified it "none" for a date.
   * A sales company has no event date worth scoping by; `active` is the axis
   * anyone actually filters on and it is already a column.
   */
  const table = useTableFilters<SalesCompany>({
    rows,
    searchText: (r) => [r.name, r.slug, r.billing_email].join(' '),
  });

  const columns = useMemo<UniversalTableColumn<SalesCompany>[]>(() => {
    const base: UniversalTableColumn<SalesCompany>[] = [
      {
        key: 'name',
        header: 'Company',
        render: (r) => (
          <Link to={`/sales/companies/${r.id}`} className="font-medium text-primary hover:underline">
            {r.name}
          </Link>
        ),
      },
      { key: 'slug', header: 'Slug', render: (r) => <code className="text-xs">{r.slug}</code> },
      { key: 'billing_email', header: 'Billing email', render: (r) => r.billing_email },
      {
        // NOT a bare recurring_commission_pct. Under net_split the engine ignores
        // that column, so printing "55%" here would state a share of GROSS when
        // the agreement is a share of NET after a per-account cost. On a screen
        // about what a partner is owed, that is the wrong number, not a rounding.
        key: 'terms',
        header: 'Commission',
        render: (r) =>
          r.commission_model === 'net_split' ? (
            <span className="text-sm">
              {r.partner_split_pct}% of net
              <span className="text-muted-foreground">
                {' '}after R{(r.cost_per_account_cents / 100).toFixed(0)}/account
                {r.term_months ? `, ${r.term_months} mo` : ''}
              </span>
            </span>
          ) : (
            <span className="text-sm">{r.recurring_commission_pct}% of revenue</span>
          ),
      },
      { key: 'active', header: 'Status', render: (r) => <StatusBadge tone={r.active ? 'active' : 'draft'}>{r.active ? 'Active' : 'Inactive'}</StatusBadge> },
    ];
    if (isAdmin) {
      base.push({
        key: 'actions',
        header: '',
        align: 'right',
        render: (r) => (
          <Button size="sm" variant="ghost" onClick={() => setEditing(r)} aria-label={`Edit ${r.name}`}>
            <Pencil className="h-4 w-4" /> Edit
          </Button>
        ),
      });
    }
    return base;
  }, [isAdmin]);

  return (
    <UniversalPage
      title="Sales Companies"
      description="Manage SightHost + external partners"
      actions={
        isAdmin && (
          <Button onClick={() => setShowCreate((v) => !v)} variant="primary">
            <Plus className="h-4 w-4" /> {showCreate ? 'Cancel' : 'Create company'}
          </Button>
        )
      }
    >
      {showCreate && isAdmin && <CreateForm onDone={() => { setShowCreate(false); reload(); }} />}

      {loading ? (
        <Card className="p-8 text-center text-sm text-muted-foreground">Loading…</Card>
      ) : (
        <>
          <div className="mb-3 flex justify-end">
            <SearchField
              value={table.q}
              onValueChange={table.setQ}
              pending={table.isSearching}
              placeholder="Search company, slug, billing email"
              className="w-full max-w-xs"
            />
          </div>
          <UniversalTable
            columns={columns}
            data={table.rows}
            getRowKey={(r) => r.id}
            caption={table.caption}
          />
        </>
      )}

      {editing && (
        <EditModal
          company={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </UniversalPage>
  );
}

function EditModal({
  company,
  onClose,
  onSaved,
}: {
  company: SalesCompany;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(company.name);
  const [billingEmail, setBillingEmail] = useState(company.billing_email);
  const [pct, setPct] = useState(String(company.recurring_commission_pct));
  const [active, setActive] = useState(company.active);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty = useMemo(() => {
    const pctNum = Number(pct);
    return (
      name.trim() !== company.name ||
      billingEmail.trim() !== company.billing_email ||
      (Number.isFinite(pctNum) && pctNum !== Number(company.recurring_commission_pct)) ||
      active !== company.active
    );
  }, [name, billingEmail, pct, active, company]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    const pctNum = Number(pct);
    if (Number.isNaN(pctNum) || pctNum < 0 || pctNum > 100) {
      setError('Recurring % must be between 0 and 100.');
      return;
    }
    if (!name.trim()) {
      setError('Company name cannot be empty.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(billingEmail.trim())) {
      setError('Billing email is malformed.');
      return;
    }

    // Build patch — only send fields that actually changed.
    const updates: Parameters<typeof salesService.updateCompany>[1] = {};
    if (name.trim() !== company.name) updates.name = name.trim();
    if (billingEmail.trim() !== company.billing_email) updates.billing_email = billingEmail.trim();
    if (pctNum !== Number(company.recurring_commission_pct)) updates.recurring_commission_pct = pctNum;
    if (active !== company.active) updates.active = active;

    if (Object.keys(updates).length === 0) {
      setError('No changes to save.');
      return;
    }

    setSubmitting(true);
    try {
      await salesService.updateCompany(company.id, updates);
      onSaved();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={`Edit ${company.name}`}
      description={`Slug: ${company.slug}`}
      size="wide"
    >
      <form onSubmit={onSubmit} className="contents">
        <ModalBody>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted-foreground">Company name</span>
              <Input value={name} onChange={(e) => setName(e.target.value)} required maxLength={200} />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted-foreground">Billing email</span>
              <Input value={billingEmail} type="email" onChange={(e) => setBillingEmail(e.target.value)} required />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-muted-foreground">Recurring commission %</span>
              <Input value={pct} type="number" min="0" max="100" step="0.01" onChange={(e) => setPct(e.target.value)} required />
              <span className="mt-1 block text-xs text-muted-foreground">Per-contract rate. Every change is audit-logged with old + new values.</span>
            </label>
            <label className="flex items-center gap-2 text-sm self-end pb-2">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
              Active
            </label>
          </div>
          {error && (
            <div className="mt-4 rounded-md border border-danger/30 bg-danger/5 p-2 text-xs text-danger">{error}</div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={submitting || !dirty}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Save changes
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}

/**
 * Strip standard SA legal-form suffixes from a company name so they don't
 * pollute the slug. Loops because some names carry both ("(Pty) Ltd") and
 * each pass only removes what's at the very end.
 *
 *   "Acme Sales (Pty) Ltd"   → "Acme Sales"
 *   "Acme Sales Pty Limited" → "Acme Sales"
 *   "Acme CC"                → "Acme"
 *   "Acme Foundation NPC"    → "Acme Foundation"
 *   "Pty Holdings"           → "Pty Holdings"   (Pty mid-name preserved)
 *   "Acme Holdings"          → "Acme Holdings"  (no suffix, untouched)
 *
 * If stripping leaves nothing (e.g. raw "Pty Ltd"), fall back to original.
 */
function stripLegalSuffix(name: string): string {
  let s = name.trim();
  let prev: string;
  do {
    prev = s;
    // (Pty) / (Pty.) / Pty / Pty.
    s = s.replace(/[\s,]*\(?\s*pty\s*\.?\s*\)?\s*$/i, '');
    // Ltd / Ltd. / Limited
    s = s.replace(/[\s,]*(ltd\.?|limited)\s*$/i, '');
    // CC / NPC / Inc / Inc. / Incorporated / SOC / RF
    s = s.replace(/[\s,]*(cc|npc|inc\.?|incorporated|soc|\(?\s*rf\s*\)?)\s*$/i, '');
  } while (s !== prev);
  return s.trim() || name.trim();
}

/**
 * Auto-derive a URL-safe slug from a company name. SA legal-form suffixes
 * are stripped first so slugs stay readable.
 *   "Acme Sales (Pty) Ltd"   → "acme-sales"
 *   "Mthethwa Marketing"     → "mthethwa-marketing"
 *   "JSL Connect (2026)"     → "jsl-connect-2026"
 *   "Sigma Foundation NPC"   → "sigma-foundation"
 * Admin can still override; this just removes the cognitive load.
 */
function deriveSlug(name: string): string {
  return stripLegalSuffix(name)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
}

function CreateForm({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [billingEmail, setBillingEmail] = useState('');
  const [pct, setPct] = useState('12.5');
  const [active, setActive] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const cleanSlug = slug.trim().toLowerCase();
    if (!/^[a-z0-9-]+$/.test(cleanSlug)) {
      setError('Slug must be lowercase letters, digits and hyphens only.');
      return;
    }
    const pctNum = Number(pct);
    if (Number.isNaN(pctNum) || pctNum < 0 || pctNum > 100) {
      setError('Recurring % must be between 0 and 100.');
      return;
    }

    setSubmitting(true);
    try {
      await salesService.createCompany({
        name: name.trim(),
        slug: cleanSlug,
        billing_email: billingEmail.trim(),
        recurring_commission_pct: pctNum,
        active,
      });
      onDone();
    } catch (e) {
      const msg = (e as Error).message;
      if (msg.toLowerCase().includes('duplicate') || msg.toLowerCase().includes('unique')) {
        setError(`A company with slug "${cleanSlug}" already exists. Pick a different slug.`);
      } else {
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Card className="mb-4 p-4">
      <form onSubmit={onSubmit} className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">Company name</span>
          <Input
            value={name}
            onChange={(e) => {
              const v = e.target.value;
              setName(v);
              // Auto-derive the slug as the user types, unless they've manually edited it.
              if (!slugTouched) setSlug(deriveSlug(v));
            }}
            placeholder="Acme Sales (Pty) Ltd"
            required
            maxLength={200}
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">
            Slug (auto-generated — internal ID, you can edit)
          </span>
          <Input
            value={slug}
            onChange={(e) => { setSlug(e.target.value); setSlugTouched(true); }}
            placeholder="acme-sales"
            required
            pattern="[a-z0-9-]+"
          />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">Billing email (commission statements)</span>
          <Input value={billingEmail} type="email" onChange={(e) => setBillingEmail(e.target.value)} placeholder="finance@acme-sales.co.za" required />
        </label>
        <label className="block">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">Recurring commission %</span>
          <Input value={pct} type="number" min="0" max="100" step="0.01" onChange={(e) => setPct(e.target.value)} required />
          <span className="mt-1 block text-xs text-muted-foreground">Indicative SightHost starting rate (12.5%). Adjust per signed contract.</span>
        </label>
        <label className="flex items-center gap-2 text-sm sm:col-span-2">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active immediately (uncheck to seed inactive — typical when contract not signed yet)
        </label>
        {error && <div className="sm:col-span-2 rounded-md border border-danger/30 bg-danger/5 p-2 text-xs text-danger">{error}</div>}
        <div className="sm:col-span-2">
          <Button type="submit" variant="primary" disabled={submitting}>
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Create company
          </Button>
        </div>
      </form>
    </Card>
  );
}
